/**
 * @fileoverview Component that renders a button to toggle the gradepoint/trend selector
 * @module GradepointSelectorButton
 */

import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsGroup, getPrimaryGradepoint, hasGroupAccess } from 'features/app/redux/context';
import { translate } from '../../../../utils/locale';
import { HeaderLink } from 'basecamp';
import { styles } from './common';
import { getGroupAccessRecords } from 'features/groups/setup/redux/selectors';
import { setCurrentKeyStage } from 'features/groups/setup/redux';
import { academicYearStandardFormat } from '../../../../utils/academicYear';
import { calculateSummitKeyStage } from '../../../../utils/summitKeyStage';

/**
 * Props interface for GradepointSelectorButton component
 * @typedef {Object} Props
 * @property {Function} toggleGradepointSelector - Function to toggle the gradepoint selector
 * @property {Groups.Setup.Trend} [selectedTrend] - Currently selected trend for groups
 * @property {boolean} isSmallScreen - Whether the screen is small
 * @property {Function} setSmallScreenMenuIsOpen - Function to set small screen menu state
 * @property {boolean} disabled - Whether the button is disabled
 */
type Props = {
	toggleGradepointSelector: () => void;
	selectedTrend: Groups.Setup.Trend | undefined;
	isSmallScreen: boolean;
	setSmallScreenMenuIsOpen: (isOpen: boolean) => void;
	disabled?: boolean;
};

/**
 * Component that renders a button to toggle the gradepoint/trend selector.
 * Only displays if user has valid subscription and has gradepoints/trends.
 * @component
 * @param {Props} props - Component props
 * @returns {JSX.Element|null} HeaderLink component or null if conditions not met
 */
const GradepointSelectorButton: FunctionComponent<Props> = ({
	toggleGradepointSelector,
	selectedTrend,
	isSmallScreen,
	setSmallScreenMenuIsOpen,
	disabled,
}) => {
	const dispatch = useDispatch();

	const isGroup = useSelector(getIsGroup);
	const pgp = useSelector(getPrimaryGradepoint);
	const groupAccess = useSelector(hasGroupAccess);

	/**
	 * Access records from Redux state containing academic year and exam level info
	 * @type {Array<{academicYear: number, id: number, examLevel: number}>}
	 */
	const accessRecords = useSelector(getGroupAccessRecords);

	/**
	 * Find the access record that matches the selected trend's group access ID
	 * @type {Object|undefined}
	 */
	const getAccessRecord =
		!!selectedTrend && accessRecords.find((record) => record.id === selectedTrend.groupAccessId);

	/**
	 * Calculate key stage and format academic year from access record
	 * @type {string|undefined}
	 */
	const keyStage = getAccessRecord && calculateSummitKeyStage(getAccessRecord.examLevel);
	const academicYear = getAccessRecord && academicYearStandardFormat(getAccessRecord!.academicYear);

	/**
	 * Effect to set initial key stage in local storage
	 */
	useEffect(() => {
		if (!localStorage.getItem('keyStageStorage') && getAccessRecord) {
			dispatch(setCurrentKeyStage({ label: keyStage, value: getAccessRecord.id }));
		}
	}, [keyStage]);

	return (
		<HeaderLink
			buttonText={
				groupAccess
					? translate('standardPage.gradepointSelector.CURRENT_TREND')
					: translate('standardPage.gradepointSelector.CURRENT_GRADEPOINT')
			}
			lozengeText={groupAccess ? `${selectedTrend?.name} - ${keyStage} ${academicYear}` : pgp?.name}
			lozengeIcon="circle-check"
			disabled={disabled}
			onClick={() => {
				toggleGradepointSelector();
				setSmallScreenMenuIsOpen(false);
			}}
			dataTest="gradepointSelectorBtn"
			{...styles(isSmallScreen)}
		/>
	);
};

export default GradepointSelectorButton;
